import React, { useReducer, useState } from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Menu, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { updateGroupTask } from "actions/groupTask/updateGroupTask";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { IconDrag } from "views/ProjectPage/RightPart/constant/Columns";
import NameInput from "./NameInput";
import { StyledMenuItem, StyledListItemIcon } from "./HeaderColumn";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteGroupTask } from "actions/groupTask/deleteGroupTask";
import AlertModal from "components/AlertModal";
import { useParams } from "react-router-dom";
import ModalCopyGroupTask from "./ModalCopyGroupTask";

const reducer = (prevState, newState) => {
  if (typeof newState === "object") return { ...prevState, ...newState };
  if (typeof newState === "function") return newState(prevState);
  return false;
};
const initialState = {
  openMenu: false,
  anchorEl: null,
  editName: false,
  openConfirm: false,
};

const ColumnNameGroup = ({
  row,
  value,
  dragHandle = {},
  onToggleAdd = () => {},
  onMouseDown = () => {},
  canEdit,
}) => {
  const group = row.original;
  const style = { fontSize: 28, fill: "#6d6e6f" };
  const [state, dispatchState] = useReducer(reducer, initialState);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { projectId } = useParams();
  const _handleOpenMenu = (event) => {
    dispatchState({ anchorEl: event.currentTarget });

    const cellHTML = event.currentTarget.closest(".td");
    if (Boolean(cellHTML)) {
      cellHTML && cellHTML.classList.add("focus");
    }
  };
  const _handleCloseMenu = () => {
    const cellHTML = state?.anchorEl?.closest(".td");
    if (Boolean(cellHTML)) {
      cellHTML && cellHTML.classList.remove("focus");
    }

    dispatchState({ anchorEl: null });
  };

  const handleDeleteGroup = () => {
    dispatch(deleteGroupTask({ groupTaskId: group.id }));
  };

  const handleDeleteConfirm = () => {
    dispatchState({ openConfirm: true, anchorEl: null });
  };
  const closeDeleteConfirm = (value) => {
    dispatchState({ openConfirm: false });
  };

  const handleEditGroupName = () => {
    _handleCloseMenu();

    setTimeout(() => {
      dispatchState({ editName: true });
    }, 10);
  };

  const closeEditOutside = () => {
    dispatchState({ editName: false });
  };

  const handleNewAddTask = (e) => {
    dispatchState({ anchorEl: null });

    setTimeout(() => {
      onToggleAdd();
    }, 0);
  };

  const handleCopyGroupTask = (e) => {
    dispatchState({ anchorEl: null });
    setOpenCopyModal(true);
  };

  const renderMenu = () => {
    return (
      <>
        <Menu
          id="menu-appbar"
          anchorEl={state.anchorEl}
          open={Boolean(state.anchorEl)}
          onClose={_handleCloseMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "start" }}
          transformOrigin={{ vertical: "top", horizontal: "start" }}
        >
          <StyledMenuItem
            style={{ marginBottom: 5 }}
            onClick={handleEditGroupName}
          >
            <StyledListItemIcon>
              <ModeEditOutlinedIcon />
            </StyledListItemIcon>
            <Typography textAlign="center">{t("EDIT")}</Typography>
          </StyledMenuItem>

          <StyledMenuItem
            onClick={handleCopyGroupTask}
            style={{ marginTop: 5 }}
          >
            <StyledListItemIcon>
              <ContentCopyIcon />
            </StyledListItemIcon>
            <Typography textAlign="center">{t("COPY_GROUP_TASK")}</Typography>
          </StyledMenuItem>

          <StyledMenuItem onClick={handleNewAddTask} style={{ marginTop: 5 }}>
            <StyledListItemIcon>
              <AddCircleOutlineIcon />
            </StyledListItemIcon>
            <Typography textAlign="center">{t("GANTT_ADD_TASK")}</Typography>
          </StyledMenuItem>

          <StyledMenuItem
            style={{ marginTop: 5 }}
            onClick={handleDeleteConfirm}
          >
            <StyledListItemIcon>
              <DeleteOutlineIcon />
            </StyledListItemIcon>
            <Typography textAlign="center">
              {t("views.calendar_page.right_part.delete_group")}
            </Typography>
          </StyledMenuItem>
        </Menu>
        <ModalCopyGroupTask
          group={group}
          open={openCopyModal}
          setOpen={setOpenCopyModal}
        />
      </>
    );
  };

  return (
    <WrapperMainGroup>
      <WrapperLeft>
        {canEdit && (
          <WrapperIconDrag
            className="drag-icon"
            {...dragHandle}
            onMouseDown={onMouseDown}
          >
            <IconDrag />
          </WrapperIconDrag>
        )}
        <WrapperButton {...row.getToggleRowExpandedProps()}>
          {!row.isExpanded ? (
            <ArrowRightRoundedIcon sx={style} />
          ) : (
            <ArrowDropDownRoundedIcon sx={style} />
          )}
        </WrapperButton>
        {/* Name group */}
        <NameGroup
          id={group.id}
          defaultValue={value}
          edit={state.editName}
          closeEditOutside={closeEditOutside}
          canEdit={canEdit}
        />
        {/* End name group */}
        {canEdit && (
          <>
            <WrapperButton className="right-side" onClick={onToggleAdd}>
              <AddRoundedIcon sx={{ fill: "#6d6e6f" }} />
            </WrapperButton>
            <WrapperButton className="right-side">
              <MoreHorizRoundedIcon
                sx={{ fill: "#6d6e6f" }}
                onClick={_handleOpenMenu}
              />
            </WrapperButton>
          </>
        )}
      </WrapperLeft>
      {canEdit && renderMenu()}
      <AlertModal
        open={state.openConfirm}
        setOpen={closeDeleteConfirm}
        content={t("DMH.COMP.ALERT_MODAL.ALERT_CONFIRM_DELETE")}
        onConfirm={() => {
          handleDeleteGroup();
        }}
      />
    </WrapperMainGroup>
  );
};

const NameGroup = ({
  id = "",
  defaultValue = "",
  edit,
  closeEditOutside = () => {},
  canEdit,
}) => {
  const [value, setValue] = React.useState(defaultValue);
  const [isEditing, setIsEditing] = React.useState(false);
  const refInput = React.useRef(null);
  const refWrapper = React.useRef(null);
  const dispatch = useDispatch();

  React.useEffect(() => {
    let timeout = setTimeout(() => {
      if (isEditing) {
        refInput.current.focus();
        refInput.current.moveToEnd();
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [isEditing]);

  React.useEffect(() => {
    setIsEditing(edit);
  }, [edit]);

  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  React.useEffect(() => {
    if (!refWrapper.current) return;
    const cellHTML = refWrapper.current.closest(".td");
    if (isEditing) {
      cellHTML && cellHTML.classList.add("focus");
    }
    return () => {
      cellHTML && cellHTML.classList.remove("focus");
    };
  }, [isEditing]);

  const _handleEditing = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const _handleBlur = (e) => {
    setIsEditing(false);
    if (e.target.value === value) return;
    setValue(e.target.value?.trim());
    dispatch(
      updateGroupTask({ groupTaskId: id, name: e.target.value?.trim() })
    );
    closeEditOutside();
  };

  const _handleKeyPress = (e) => {
    if (e.which === 13 && !e.shiftKey) {
      refInput.current.blur();
      closeEditOutside();
    }
  };

  if (isEditing)
    return (
      <div ref={refWrapper} style={{ maxWidth: "calc(100%)" }}>
        <NameInput
          isGroup
          ref={refInput}
          border="none"
          placeholder={"Write a task name"}
          maxWidth="calc(100% - 15px)"
          defaultValue={value}
          onBlur={_handleBlur}
          onKeyPress={_handleKeyPress}
          canEdit={canEdit}
        />
      </div>
    );
  return (
    <>
      <WrapperName onClick={_handleEditing}>
        <StyledHeadingGroup>{value}</StyledHeadingGroup>
      </WrapperName>
    </>
  );
};

const WrapperLeft = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  .right-side {
    visibility: hidden;
  }
  &:hover {
    .right-side {
      visibility: visible;
    }
  }
`;

const WrapperIconDrag = styled.div`
  position: absolute;
  top: 50%;
  height: 19.5px;
  transform: translateY(-50%);
  visibility: hidden;
  position: absolute;
  left: 8px;
`;

const WrapperMainGroup = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  .wrapper-right {
    display: none;
  }
  &:hover {
    .wrapper-right {
      display: flex;
    }
  }
`;

const WrapperButton = styled.div`
  height: 28px;
  min-height: 28px;
  min-width: 28px;
  width: 28px;
  border-radius: 6px;
  margin-right: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  fill: #6f7782;

  &.right-side {
    margin-left: 4px;
  }
  &:hover {
    background: #1507260a;
    fill: #151b26;
    cursor: pointer;
  }
`;

const TextEllipsis = styled.span`
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e1f21;
  max-width: 100%;
  display: block;
`;

const StyledHeadingGroup = styled(TextEllipsis)`
  font-size: 16px;
  font-weight: 500;
  line-height: 36px;
  height: 36px;
  margin-left: 0;
  min-width: 1px;
  outline: none;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WrapperName = styled.div`
  min-height: 36px;
  font-size: 15px;
  font-weight: 400;
  margin-left: 0;
  min-width: 120px;
  outline: none;
  cursor: pointer;
`;

export default ColumnNameGroup;
